import React, { useState, useEffect, useRef } from 'react';
import * as paymentService from '../services/paymentService';
import { useWallet } from '../context/WalletContext';
import QRCode from 'react-qr-code';
import { useNavigate } from 'react-router-dom';
import '../styles/payment-component.css';

/**
 * Enhanced Payment Component
 * Handles the payment process for RWA purchases with improved UX and compatibility
 */
const PaymentComponent = ({ 
  product, 
  selectedNodeId, 
  onPaymentSuccess, 
  onPaymentError,
  onClose
}) => {
  const navigate = useNavigate();
  const { wallet } = useWallet();
  const [paymentOrder, setPaymentOrder] = useState(null);
  const [selectedNetwork, setSelectedNetwork] = useState('bsc');
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const [isLoadingOrder, setIsLoadingOrder] = useState(false);
  const [error, setError] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [statusCheckInterval, setStatusCheckInterval] = useState(null);
  const [signatureTimestamp, setSignatureTimestamp] = useState(0);
  const [showCopyToast, setShowCopyToast] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState({
    address: '',
    amount: '',
    currency: 'USDT',
    qrValue: ''
  });
  
  // Add a state to track payment status
  const [paymentStatus, setPaymentStatus] = useState('pending'); // 'pending', 'processing', 'success', 'failed'
  
  // Reference to track if component is mounted
  const isMounted = useRef(true);
  
  // Clean up on unmount
  useEffect(() => {
    return () => {
      isMounted.current = false;
      if (statusCheckInterval) {
        clearInterval(statusCheckInterval);
      }
    };
  }, [statusCheckInterval]);
  
  // Format time remaining
  const formatTimeRemaining = (seconds) => {
    if (seconds <= 0) return '00:00:00';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    return [hours, minutes, secs]
      .map(v => v < 10 ? `0${v}` : v)
      .join(':');
  };

  // Navigate to portfolio/asset management page
  const navigateToPortfolio = () => {
    navigate('/portfolio');
    if (onClose) onClose();
  };
  
  // Create payment order with signature throttling
  const createOrder = async () => {
    setIsCreatingOrder(true);
    setError(null);
    
    try {
      // Check if we need to throttle the signature request
      const currentTime = Date.now();
      const timeSinceLastSignature = currentTime - signatureTimestamp;
      
      // Only request a new signature if it's been more than 60 seconds
      // or if we haven't requested one yet
      const needsNewSignature = timeSinceLastSignature > 60000 || signatureTimestamp === 0;
      
      // Use the selected payment network to determine the correct network ID
      const networkIdForPayment = selectedNetwork === 'bsc' ? 2 : (selectedNetwork === 'solana' ? 3 : 1);
      
      console.log('Creating payment order:', {
        wallet: wallet.address,
        paymentNetwork: selectedNetwork,
        networkIdForPayment: networkIdForPayment,
        walletChainId: wallet.chainId, 
        walletNetwork: wallet.network
      });
      
      const response = await paymentService.createPaymentOrder(
        wallet.address,
        selectedNetwork, // Use selected payment network instead of wallet's network
        wallet.type,
        product.id,
        product.name,
        product.price,
        selectedNodeId,
        selectedNetwork,
        needsNewSignature // Pass flag to indicate if we need a new signature
      );
      
      if (response.success) {
        // If we used a new signature, update the timestamp
        if (needsNewSignature) {
          setSignatureTimestamp(currentTime);
        }
        
        setPaymentOrder(response.data);
        
        // Set time remaining for countdown
        if (response.data.time_remaining) {
          setTimeRemaining(response.data.time_remaining);
        }
        
        // Set payment details
        if (response.data.payment_qr_code) {
          const qrData = response.data.payment_qr_code;
          setPaymentDetails({
            address: qrData.address,
            amount: qrData.amount,
            currency: qrData.currency,
            qrValue: `${qrData.currency}:${qrData.address}?amount=${qrData.amount}`
          });
        }
        
        // Start status check interval
        startStatusCheckInterval(response.data.order_id);
      } else {
        setError(response.message || 'Failed to create payment order');
        if (onPaymentError) {
          onPaymentError(response.message || 'Failed to create payment order');
        }
      }
    } catch (error) {
      console.error('Error creating payment order:', error);
      setError(error.message || 'Failed to create payment order');
      if (onPaymentError) {
        onPaymentError(error.message || 'Failed to create payment order');
      }
    } finally {
      if (isMounted.current) {
        setIsCreatingOrder(false);
      }
    }
  };
  
  // Change payment network
  const handleNetworkChange = (network) => {
    // If we already have a payment order, we need to create a new one
    if (paymentOrder) {
      setSelectedNetwork(network);
      // Clear current order
      setPaymentOrder(null);
      // Stop status check interval
      clearInterval(statusCheckInterval);
      setStatusCheckInterval(null);
    } else {
      setSelectedNetwork(network);
    }
  };
  
  // Start interval to check payment status with reduced frequency
  const startStatusCheckInterval = (orderId) => {
    // Clear any existing interval
    if (statusCheckInterval) {
      clearInterval(statusCheckInterval);
    }
    
    // Start new interval - check every 60 seconds
    // This reduces the frequency of signature requests
    const interval = setInterval(() => {
      checkPaymentStatus(orderId);
    }, 60000); // 60 seconds instead of 20 seconds
    
    setStatusCheckInterval(interval);
  };
  
  // Check payment status
  const checkPaymentStatus = async (orderId) => {
    try {
      // Check if we need to throttle the signature request
      const currentTime = Date.now();
      const timeSinceLastSignature = currentTime - signatureTimestamp;
      
      // Only request a new signature if it's been more than 60 seconds
      // or if we haven't requested one yet
      const needsNewSignature = timeSinceLastSignature > 60000 || signatureTimestamp === 0;
      
      // If we're throttling signatures, skip this check
      if (!needsNewSignature) {
        console.log('Skipping payment status check to avoid frequent signature requests');
        return;
      }
      
      // Use the correct network ID for the selected payment network
      const networkIdForPayment = selectedNetwork === 'bsc' ? 2 : (selectedNetwork === 'solana' ? 3 : 1);
      
      const response = await paymentService.checkPaymentStatus(
        orderId,
        wallet.address,
        networkIdForPayment, // Use payment network-specific ID
        wallet.type
      );
      
      // Update signature timestamp
      setSignatureTimestamp(currentTime);
      
      if (response.success) {
        // If payment is confirmed
        if (response.data.status === 'confirmed' || response.data.status === 'completed') {
          // Stop interval
          clearInterval(statusCheckInterval);
          setStatusCheckInterval(null);
          
          // Set payment status to success
          setPaymentStatus('success');
          
          // Call success callback
          if (onPaymentSuccess) {
            onPaymentSuccess(response.data);
          }
        }
        
        // If payment is expired
        if (response.data.status === 'expired') {
          // Stop interval
          clearInterval(statusCheckInterval);
          setStatusCheckInterval(null);
          
          // Show error
          setError('Payment order has expired. Please create a new order.');
        }
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
    }
  };
  
  // Load existing order details
  const loadOrderDetails = async (orderId) => {
    setIsLoadingOrder(true);
    setError(null);
    
    try {
      // Check if we need to throttle the signature request
      const currentTime = Date.now();
      const timeSinceLastSignature = currentTime - signatureTimestamp;
      
      // Only request a new signature if it's been more than 60 seconds
      // or if we haven't requested one yet
      const needsNewSignature = timeSinceLastSignature > 60000 || signatureTimestamp === 0;
      
      // Use the correct network ID for the selected payment network
      const networkIdForPayment = selectedNetwork === 'bsc' ? 2 : (selectedNetwork === 'solana' ? 3 : 1);
      
      const response = await paymentService.getPaymentOrderDetails(
        orderId,
        wallet.address,
        networkIdForPayment, // Use payment network-specific ID
        wallet.type,
        needsNewSignature // Pass flag to indicate if we need a new signature
      );
      
      if (response.success) {
        // If we used a new signature, update the timestamp
        if (needsNewSignature) {
          setSignatureTimestamp(currentTime);
        }
        
        setPaymentOrder(response.data);
        
        // Set time remaining for countdown
        if (response.data.time_remaining) {
          setTimeRemaining(response.data.time_remaining);
        }
        
        // Set payment details
        if (response.data.payment_qr_code) {
          const qrData = response.data.payment_qr_code;
          setPaymentDetails({
            address: qrData.address,
            amount: qrData.amount,
            currency: qrData.currency,
            qrValue: `${qrData.currency}:${qrData.address}?amount=${qrData.amount}`
          });
        }
        
        // Start status check interval
        startStatusCheckInterval(response.data.order_id);
      } else {
        setError(response.message || 'Failed to load payment order');
      }
    } catch (error) {
      console.error('Error loading payment order details:', error);
      setError(error.message || 'Failed to load payment order');
    } finally {
      if (isMounted.current) {
        setIsLoadingOrder(false);
      }
    }
  };
  
  // Manual status check with button
  const manualCheckStatus = () => {
    if (paymentOrder) {
      checkPaymentStatus(paymentOrder.order_id);
    }
  };
  
  // Countdown timer effect
  useEffect(() => {
    if (timeRemaining <= 0) return;
    
    const countdown = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(countdown);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(countdown);
  }, [timeRemaining]);
  
  // Effect to create order on component mount
  useEffect(() => {
    if (wallet && wallet.connected && product && selectedNodeId && !paymentOrder) {
      createOrder();
    }
  }, [wallet, product, selectedNodeId, selectedNetwork]);
  
  // Copy address to clipboard with improved UX
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        // Show the toast notification
        setShowCopyToast(true);
        
        // Hide the toast after 2 seconds
        setTimeout(() => {
          if (isMounted.current) {
            setShowCopyToast(false);
          }
        }, 2000);
      })
      .catch(err => console.error('Failed to copy:', err));
  };
  
  // Render success screen after payment completion
  const renderSuccessScreen = () => (
    <div className="payment-success">
      <div className="success-icon">
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="#10b981" strokeWidth="2"/>
          <path d="M8 12L11 15L16 9" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
      <h3>Payment Successful!</h3>
      <p>Your purchase of {product.name} has been completed.</p>
      
      <div className="success-details">
        <div className="success-item">
          <span className="success-label">Order ID:</span>
          <span className="success-value">{paymentOrder.order_id}</span>
        </div>
        <div className="success-item">
          <span className="success-label">Amount:</span>
          <span className="success-value">{paymentDetails.amount} {paymentDetails.currency}</span>
        </div>
        <div className="success-item">
          <span className="success-label">Date:</span>
          <span className="success-value">{new Date().toLocaleString()}</span>
        </div>
      </div>
      
      <p className="success-message">
        Your RWA device will be activated shortly. You can view all your assets in your portfolio.
      </p>
      
      <div className="success-actions">
        <button className="btn btn-outline" onClick={navigateToPortfolio}>View My Portfolio</button>
        <button className="btn" onClick={onClose}>Continue</button>
      </div>
    </div>
  );
  
  // Render loading state
  if (isCreatingOrder || isLoadingOrder) {
    return (
      <div className="payment-component">
        <div className="payment-loading">
          <div className="spinner"></div>
          <p>{isCreatingOrder ? 'Creating payment order...' : 'Loading payment details...'}</p>
        </div>
      </div>
    );
  }
  
  // Render error state
  if (error) {
    return (
      <div className="payment-component">
        <div className="payment-error">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="#ef4444" strokeWidth="2"/>
            <path d="M12 8V12" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/>
            <path d="M12 16H12.01" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <h3>Payment Error</h3>
          <p>{error}</p>
          <div className="payment-actions">
            <button className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button className="btn" onClick={() => createOrder()}>Try Again</button>
          </div>
        </div>
      </div>
    );
  }
  
  // Render success state if payment is successful
  if (paymentStatus === 'success') {
    return (
      <div className="payment-component">
        {renderSuccessScreen()}
      </div>
    );
  }
  
  // Render main payment component
  return (
    <div className="payment-component">
      {!paymentOrder ? (
        <div className="network-selection">
          <h3>Select Payment Network</h3>
          <p>Choose which network you want to use for your USDT payment.</p>
          
          <div className="network-options">
            <button 
              className={`network-option ${selectedNetwork === 'bsc' ? 'selected' : ''}`}
              onClick={() => handleNetworkChange('bsc')}
            >
              <div className="network-icon bsc">
                <svg width="24" height="24" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="32" height="32" rx="6" fill="#F0B90B"/>
                  <path d="M16 7L18.5 9.5L13.5 14.5L11 12L16 7Z" fill="white"/>
                  <path d="M19.5 10.5L22 13L13.5 21.5L11 19L19.5 10.5Z" fill="white"/>
                  <path d="M10 13L12.5 15.5L10 18L7.5 15.5L10 13Z" fill="white"/>
                  <path d="M22 19L24.5 21.5L22 24L19.5 21.5L22 19Z" fill="white"/>
                </svg>
              </div>
              <div className="network-info">
                <h4>Binance Smart Chain</h4>
                <p>BEP-20 USDT</p>
              </div>
            </button>
            
            <button 
              className={`network-option ${selectedNetwork === 'solana' ? 'selected' : ''}`}
              onClick={() => handleNetworkChange('solana')}
            >
              <div className="network-icon solana">
                <svg width="24" height="24" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="32" height="32" rx="6" fill="#551BF9"/>
                  <path d="M23 10H15C12.2 10 10 12.2 10 15C10 17.8 12.2 20 15 20H17V22H21V16H15C14.4 16 14 15.6 14 15C14 14.4 14.4 14 15 14H23V10Z" fill="white"/>
                </svg>
              </div>
              <div className="network-info">
                <h4>Solana</h4>
                <p>SPL USDT</p>
              </div>
            </button>
          </div>
          
          <div className="network-selection-footer">
            <button 
              className="btn btn-outline" 
              onClick={onClose}
              disabled={isCreatingOrder}
            >
              Cancel
            </button>
            <button 
              className="btn" 
              onClick={createOrder}
              disabled={isCreatingOrder}
            >
              Continue with {selectedNetwork === 'bsc' ? 'BSC' : 'Solana'}
            </button>
          </div>
        </div>
      ) : (
        <div className="payment-details">
          <div className="payment-header">
            <h3>Payment Details</h3>
            {timeRemaining > 0 && (
              <div className="payment-timer">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                  <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>{formatTimeRemaining(timeRemaining)}</span>
              </div>
            )}
          </div>
          
          <div className="payment-summary">
            <div className="payment-summary-item">
              <span className="summary-label">Product:</span>
              <span className="summary-value">{product.name}</span>
            </div>
            <div className="payment-summary-item">
              <span className="summary-label">Amount:</span>
              <span className="summary-value">{paymentDetails.amount} {paymentDetails.currency}</span>
            </div>
            <div className="payment-summary-item">
              <span className="summary-label">Order ID:</span>
              <span className="summary-value">{paymentOrder.order_id}</span>
            </div>
            <div className="payment-summary-item">
              <span className="summary-label">Network:</span>
              <span className="summary-value">{selectedNetwork === 'bsc' ? 'Binance Smart Chain (BEP-20)' : 'Solana (SPL)'}</span>
            </div>
            <div className="payment-summary-item">
              <span className="summary-label">Status:</span>
              <span className={`status-badge ${paymentOrder.status}`}>{paymentOrder.status}</span>
            </div>
          </div>
          
          <div className="payment-qr-container">
            <div className="qr-code">
              {paymentDetails.qrValue && (
                <QRCode 
                  value={paymentDetails.qrValue}
                  size={160}
                  level="H"
                  bgColor="#ffffff"
                  fgColor="#000000"
                />
              )}
            </div>
            
            <div className="payment-address-container">
              <p className="address-label">Send USDT to this address:</p>
              <div className="payment-address">
                <div className="address">{paymentDetails.address}</div>
                <button 
                  className="copy-button" 
                  onClick={() => copyToClipboard(paymentDetails.address)}
                  aria-label="Copy address"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 4V16C8 16.5304 8.21071 17.0391 8.58579 17.4142C8.96086 17.7893 9.46957 18 10 18H18C18.5304 18 19.0391 17.7893 19.4142 17.4142C19.7893 17.0391 20 16.5304 20 16V7.242C20 6.97556 19.9467 6.71181 19.8433 6.46624C19.7399 6.22068 19.5885 5.99824 19.398 5.812L16.188 2.602C16.0017 2.4115 15.7793 2.25992 15.5338 2.15654C15.2882 2.05316 15.0244 1.99989 14.758 2H10C9.46957 2 8.96086 2.21071 8.58579 2.58579C8.21071 2.96086 8 3.46957 8 4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M16 18V20C16 20.5304 15.7893 21.0391 15.4142 21.4142C15.0391 21.7893 14.5304 22 14 22H6C5.46957 22 4.96086 21.7893 4.58579 21.4142C4.21071 21.0391 4 20.5304 4 20V8C4 7.46957 4.21071 6.96086 4.58579 6.58579C4.96086 6.21071 5.46957 6 6 6H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
              
              <div className="address-note">
                <strong>Note:</strong> Send exactly {paymentDetails.amount} {paymentDetails.currency} on the {selectedNetwork === 'bsc' ? 'BEP-20' : 'SPL'} network.
              </div>
            </div>
          </div>
          
          <div className="payment-instructions">
            <h4>Payment Instructions</h4>
            <ol>
              <li>Send exactly <strong>{paymentDetails.amount} {paymentDetails.currency}</strong> to the address above</li>
              <li>Make sure you're sending on the <strong>{selectedNetwork === 'bsc' ? 'Binance Smart Chain (BEP-20)' : 'Solana (SPL)'}</strong> network</li>
              <li>Wait for transaction confirmation (typically 5-10 minutes)</li>
              <li>Once payment is confirmed, your purchase will be automatically bound to your selected node</li>
            </ol>
            
            <div className="payment-note">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                <path d="M12 8V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <path d="M12 16H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <p>Note: Payment confirmation may take up to 24 hours. You'll receive a confirmation email once your payment is processed. We automatically check payment status every 1 minute, but you can also manually check using the button below.</p>
            </div>
          </div>
          
          <div className="payment-actions">
            <button className="btn btn-outline" onClick={onClose}>Close</button>
            <button 
              className="btn" 
              onClick={manualCheckStatus}
            >
              Check Payment Status
            </button>
          </div>
        </div>
      )}
      
      {/* Copy toast notification */}
      {showCopyToast && (
        <div className="copy-toast">
          Address copied to clipboard!
        </div>
      )}
    </div>
  );
};

export default PaymentComponent;
